/**
 * AppShell — wraps every authenticated route.
 *
 * Owns (WP-P1): the left navigation rail / mobile drawer (driven by
 * `src/navigation.ts`), the single WebSocket connection, the notification
 * bell + panel, toasts, confirm dialogs, the live-status pill and the
 * Cmd/Ctrl+K command palette.
 *
 * Layout: rail is fixed on the left at md+ (60px collapsed / 240px pinned,
 * tracked via the `--rail-width` CSS variable); page content gets a matching
 * padding-left. Below md the rail is hidden and a floating hamburger opens
 * NavDrawer.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import LeftNavRail from '../components/LeftNavRail'
import { NotificationPanel } from '../components/NotificationPanel'
import { CommandPalette } from './CommandPalette'
import { useShellWebSocket } from './useShellWebSocket'
import { useAuthStore } from '../stores/authStore'
// WP-P6: persistent "viewing as <account>" banner for SUPERADMIN impersonation.
import ImpersonationBanner from '../features/platform/ImpersonationBanner'

export default function AppShell({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const connectionState = useShellWebSocket(isAuthenticated)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const openPalette = useCallback(() => setPaletteOpen(true), [])
  const closePalette = useCallback(() => setPaletteOpen(false), [])

  // Global Cmd/Ctrl+K — works from inside inputs too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(o => !o)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="min-h-screen bg-canvas">
      <LeftNavRail connectionState={connectionState} onOpenPalette={openPalette} />
      <NotificationPanel />
      <CommandPalette open={paletteOpen} onClose={closePalette} />
      <main
        id="main"
        className="min-h-screen pl-0 md:pl-[var(--rail-width,60px)] transition-[padding-left] duration-200"
      >
        <ImpersonationBanner />
        {children}
      </main>
    </div>
  )
}
